import onlineOnlyImage from "../images/online_only.png";
import { useCurrentInventoryId } from "../hooks/url";
import { useCurrentDatabaseManager } from "../hooks/database/useDatabases";
import { useCallback } from "react";
import { ImageObject, LegacyImageObject, ModernImageObject, UnsavedImageObject } from "../hooks/database/types/inventories";

export function isModernImageObject(obj: ImageObject): obj is ModernImageObject {
  return "imageId" in obj && "thumbnailId" in obj;
}
export function isUnsavedImageObject(obj: ImageObject): obj is UnsavedImageObject {
  return "imageUri" in obj && "thumbnailUri" in obj;
}
export function isLegacyImageObject(obj: ImageObject): obj is LegacyImageObject {
  return "src" in obj;
}

export function useGetImage() {
  const { inventoryId } = useCurrentInventoryId();
  const mngr = useCurrentDatabaseManager();

  return useCallback(
    async (obj: ImageObject, thumbnail: boolean = false) => {
      if (!mngr || !inventoryId) {
        throw new Error("Attempted to get image with invalid parameters");
      }

      if (isUnsavedImageObject(obj)) {
        // for images not yet persisted
        return thumbnail ? obj.thumbnailUri : obj.imageUri;
      } else if (!isModernImageObject(obj)) {
        // for legacy image objects (that just contain the image as a data uri)
        return obj.src;
      }

      const attachment = thumbnail ? await mngr.getThumbnail(inventoryId, obj.thumbnailId) : await mngr.getImage(inventoryId, obj.imageId);
      if (attachment) {
        return URL.createObjectURL(attachment as Blob);
      } else {
        return onlineOnlyImage;
      }
    },
    [inventoryId, mngr]
  );
}

export async function resize(dataURI: string, maxSize: number) {
  return new Promise<string>((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => {
      const canvas = document.createElement("canvas");
      let width = image.width,
        height = image.height;

      if (width > height) {
        if (width > maxSize) {
          height *= maxSize / width;
          width = maxSize;
        }
      } else if (height > maxSize) {
        width *= maxSize / height;
        height = maxSize;
      }
      canvas.width = width;
      canvas.height = height;

      canvas.getContext("2d")!.drawImage(image, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.8));
    });
    image.addEventListener("error", reject);
    image.src = dataURI;
  });
}
